import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { TopNavigation } from './TopNavigation';
import { BottomNavigation } from './BottomNavigation';

interface SubPageLayoutProps {
  /** Page title, centered in the header */
  title: string;
  /** Custom back action. Defaults to navigate(-1) */
  onBack?: () => void;
  /**
   * Accessible label for the back arrow. Pass a translated string
   * (`t('common.back')`); defaults to English for callers that predate i18n
   * reaching this layout.
   */
  backLabel?: string;
  /** Right-side slot in the header (optional icon/button) */
  headerRight?: React.ReactNode;
  /** Hide the bottom navigation bar */
  hideBottomNav?: boolean;
  /**
   * Hide the top navigation bar (the sOSSiety logo bar).
   * Use on deep-drill secondary pages that should show only the back-arrow header.
   */
  hideTopNav?: boolean;
  /** Page content */
  children: React.ReactNode;
}

/**
 * Standard layout for sub-pages (drill-down screens).
 * Matches the pattern used in Connections, Achievements, Posts.
 *
 * Handles:
 * - TopNavigation (subtle, fixed) + fixed sub-page header (back arrow + title)
 * - Content scrolls underneath both fixed bars
 * - BottomNavigation (fixed, can be hidden)
 *
 * Fixed-bar stack (top → bottom):
 *   z-50  TopNavigation         h-16 (64 px)   — only when !hideTopNav
 *   z-40  Sub-page header       h-[52px]
 *   —     Scrollable content    pt offsets both bars
 *
 * Usage:
 * ```tsx
 * <SubPageLayout title={t('some.title')}>
 *   <p>Content here — no need for px-4 or pt-*</p>
 * </SubPageLayout>
 * ```
 */
export const SubPageLayout: React.FC<SubPageLayoutProps> = ({
  title,
  onBack,
  backLabel = 'Back',
  headerRight,
  hideBottomNav = false,
  hideTopNav = false,
  children,
}) => {
  const navigate = useNavigate();
  const handleBack = onBack ?? (() => navigate(-1));

  // TopNavigation is h-16 (64 px). Sub-page header is h-[52px].
  // When TopNavigation is hidden the sub-page header sits at top-0.
  const subHeaderTop  = hideTopNav ? 'top-0'  : 'top-16';
  // Content must clear the fixed bar(s) plus 16px breathing room so the
  // sub-header's border-b doesn't visually glue to the first content row.
  //   hideTopNav   → 52px (sub-header) + 16px = 68px
  //   default      → 116px (both bars) + 16px = 132px
  const contentPadTop = hideTopNav ? 'pt-[68px]' : 'pt-[132px]';

  return (
    <>
      {!hideTopNav && <TopNavigation variant="subtle" />}

      {/* ── Fixed sub-page header (back arrow + centered title) ── */}
      <div
        className={`fixed left-0 right-0 z-40 ${subHeaderTop} bg-[#160c0c] border-b border-white/[0.06]`}
        style={{ maxWidth: '100vw' }}
      >
        <div className="relative flex items-center justify-between px-4 py-3.5">
          <button
            onClick={handleBack}
            className="p-1 text-white/80 hover:text-white transition-colors"
            aria-label={backLabel}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>

          <h1 className="absolute left-1/2 -translate-x-1/2 text-base font-semibold truncate max-w-[60%]">
            {title}
          </h1>

          {/* Right slot — keeps title centered when present */}
          <div className="w-7 flex justify-end">
            {headerRight ?? null}
          </div>
        </div>
      </div>

      {/* ── Scrollable content ── */}
      <div
        className="min-h-screen bg-sossiety-dark text-white"
        style={{ width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}
      >
        <div
          className={`w-full px-4 pb-28 ${contentPadTop}`}
          style={{ maxWidth: '100vw', boxSizing: 'border-box' }}
        >
          {children}
        </div>
      </div>

      {!hideBottomNav && <BottomNavigation />}
    </>
  );
};
